import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate, useOutletContext } from "react-router";
import { useSelector } from "react-redux";
import { loadPaymentWidget } from "@tosspayments/payment-widget-sdk";
import uuid from "react-uuid";
import axios from "axios";
import { toast } from "react-toastify";
import { FaChevronLeft } from "react-icons/fa6";

import Total from "./order/total";
import Address from "./order/address";
import Products from "./order/products";
import AddressModal from "./addresses/addressModal";
import SelectAddressModal from "./order/selectAddressModal";
import CashReceipt from "./order/cashReceipt";
import PaymentMethod from "./order/paymentMethod";
import Loading from "../../../../common/loading";
import { OrderContainer } from "./styled";

const MAX_RETRY = 10;
const RETRY_DELAY = 500;
const clientKey = "test_gck_6bJXmgo28eDjx71vzNp6rLAnGKWx";

const Order = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ query: "(max-width:767px)" });
  const user = useSelector((state) => state?.user?.data?.[0]);

  const { url, openModal, closeModal } = useOutletContext();

  const widgetRef = useRef(null);
  const widgetContainerRef = useRef(null);

  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [target, setTarget] = useState(null);
  const [paymentType, setPaymentType] = useState("TOSS");
  const [widgetLoaded, setWidgetLoaded] = useState(false);

  const sessionItems = sessionStorage.getItem("orderItems");
  const customerKey = uuid();

  const products = useMemo(() => {
    const fromState = location.state?.items;
    if (fromState?.length) return fromState;

    try {
      const parsed = JSON.parse(sessionItems || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [location.state?.items]);

  const totalPrice = useMemo(() => {
    return products.reduce((total, group) => {
      return (
        total +
        group.products.reduce((sum, p) => {
          const optionTotal = (p.options || []).reduce(
            (optSum, opt) => optSum + (opt.price ?? 0) * (opt.qty ?? 1),
            0,
          );
          return sum + (p.productPrice + optionTotal) * p.quantity;
        }, 0)
      );
    }, 0);
  }, [products]);

  const deliveryFee = 3000;
  const totalPayable = totalPrice + deliveryFee;

  useEffect(() => {
    if (!user) {
      navigate(`/shop/${url}/login`);
    } else if (!products.length) {
      navigate("/shop/mypage");
    } else {
      sessionStorage.setItem("orderItems", JSON.stringify(products));
      fetchAddresses();
    }
  }, [user]);

  useEffect(() => {
    const saved = sessionStorage.getItem("orderAddress");
    if (saved) {
      try {
        setSelected(JSON.parse(saved));
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (selected) {
      sessionStorage.setItem("orderAddress", JSON.stringify(selected));
    }
  }, [selected]);

  useEffect(() => {
    if (paymentType !== "TOSS" || !widgetContainerRef.current || !products.length || !user) return;

    const timeout = setTimeout(() => {
      requestAnimationFrame(() => {
        initPaymentWidget();
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [products, paymentType, user]);

  const fetchAddresses = async () => {
    try {
      const res = await axios.post("/erp/address/get", { mberNo: user.mberNo });
      const data = res?.data || [];
      setAddresses(data);
      setSelected(data.find((item) => item.isDefault) || data[0]);
    } catch (err) {
      console.error("주소 조회 실패", err);
    }
  };

  const initPaymentWidget = async (retry = 0) => {
    const amount = Number(totalPayable || 0);
    if (amount <= 0) return;

    try {
      const widget = await loadPaymentWidget(clientKey, customerKey);
      await widget.renderPaymentMethods("#payment-widget", {
        value: amount,
        ...getOrderDetails(),
      });
      widgetRef.current = widget;
      setWidgetLoaded(true);
    } catch (err) {
      if (retry < MAX_RETRY) {
        setTimeout(() => initPaymentWidget(retry + 1), RETRY_DELAY);
      } else {
        toast.error("결제 위젯 로딩 실패. 새로고침 해주세요.");
      }
    }
  };

  const getOrderDetails = () => ({
    orderId: uuid(),
    orderName: "주문 결제",
    customerName: user?.mberNm || "고객",
    customerEmail: user?.email || "noemail@example.com",
  });

  const handlePayment = async () => {
    if (!validateOrder()) return;

    try {
      sessionStorage.setItem("paymentMethod", paymentType);
      await widgetRef.current.requestPayment({
        ...getOrderDetails(),
        // successUrl: `http://localhost:3001/shop/mypage/order/success`,
        // failUrl: `http://localhost:3001/shop/mypage/order/fail`,
        successUrl: `https://1472.ai/shop/mypage/order/success`,
        failUrl: `https://1472.ai/shop/mypage/order/fail`,
      });
    } catch (err) {
      alert(err.message);
    }
  };

  const validateOrder = () => {
    if (!selected) return toast.error("배송지를 선택해주세요.");
    if (!products.length) return toast.error("상품이 존재하지 않습니다.");
    if (!totalPayable || isNaN(totalPayable)) return toast.error("결제금액이 유효하지 않습니다.");
    if (paymentType === "TOSS" && !widgetRef.current) return toast.error("토스 결제 준비가 안 됨.");
    return true;
  };

  const toggleModal = (item = null, type = null) => {
    setTarget(item);
    setModalType(type);
  };

  const getModalComponent = () => {
    switch (modalType) {
      case "form":
        return (
          <AddressModal
            openModal={openModal}
            closeModal={closeModal}
            target={target}
            toggleModal={toggleModal}
            getAddresses={fetchAddresses}
            addresses={addresses}
          />
        );
      case "select":
        return (
          <SelectAddressModal
            toggleModal={toggleModal}
            addresses={addresses}
            selected={selected}
            setSelected={setSelected}
          />
        );
      default:
        return null;
    }
  };
  const isActive = paymentType !== "TOSS" || widgetLoaded;

  return (
    <OrderContainer>
      {!isActive && <Loading />}
      {modalType && getModalComponent()}

      <h2>
        {isMobile && (
          <button onClick={() => navigate(`/shop/mypage`)}>
            <FaChevronLeft />
          </button>
        )}
        <span>주문서</span>
      </h2>

      <div className="contents">
        <div className="info">
          <Address addresses={addresses} selection={selected} setAddressModal={setModalType} />
          <Products products={products} />
          <PaymentMethod
            paymentType={paymentType}
            setPaymentType={setPaymentType}
            widgetContainerRef={widgetContainerRef}
            isActive={isActive}
          />
          <CashReceipt />
        </div>

        <Total
          totalPrice={totalPrice}
          deliveryFee={deliveryFee}
          totalPayable={totalPayable}
          onClickPay={handlePayment}
          paymentType={paymentType}
          isActive={isActive}
        />
      </div>
    </OrderContainer>
  );
};

export default Order;
